import React, { useEffect, useRef, useState } from "react";
import { BASE_URL } from "../config";
import { Check, ChevronDown, FileDown } from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const CallSummaryTable = ({ dateRange, onRowClick }) => {
  const [summaryData, setSummaryData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [showAgentDropdown, setShowAgentDropdown] = useState(false);
  const [viewMode, setViewMode] = useState(
    () => localStorage.getItem("viewMode") || "queue"
  );

  const dropdownRef = useRef(null);

  /* ================= LOAD AGENTS ================= */
  useEffect(() => {
    const loadAgents = async () => {
      try {
        const res = await fetch("/extensions.json");
        const data = await res.json();
        setAgents(data.filter((item) => item.type === "User"));
      } catch (err) {
        console.error("Failed to load extensions.json:", err);
      }
    };
    loadAgents();
  }, []);

  /* ================= CLOSE DROPDOWN ================= */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowAgentDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ================= FETCH SUMMARY ================= */
  useEffect(() => {
    const fetchSummary = async () => {
      setIsLoading(true);
      try {
        const addHoursUTC = (isoString, hours) => {
          if (!isoString) return "";
          const date = new Date(
            isoString.endsWith("Z") ? isoString : isoString + "Z"
          );
          date.setUTCHours(date.getUTCHours() + hours);
          return date.toISOString();
        };

        const start = addHoursUTC(dateRange.startDate, 5);
        const end = addHoursUTC(dateRange.endDate, 5);

        let url = "";
        if (viewMode === "queue") {
          url = `${BASE_URL}get_summary.php?startDate=${encodeURIComponent(
            start
          )}&endDate=${encodeURIComponent(end)}`;
        } else {
          const query = new URLSearchParams({
            startDate: start,
            endDate: end,
          });
          selectedAgents.forEach((a) => query.append("agents[]", a));
          url = `${BASE_URL}get_agent_summary.php?${query.toString()}`;
        }

        const res = await fetch(url, { credentials: "include" });
        const json = await res.json();
        setSummaryData(
          json.status === "success" ? json.summary || [] : []
        );
      } catch (err) {
        console.error("Failed to fetch summary:", err);
        setSummaryData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSummary();
  }, [dateRange, viewMode, selectedAgents]);

  /* ================= EXPORT ================= */
  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(summaryData),
      viewMode === "queue" ? "MC Summary" : "Agent Summary"
    );
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([wbout], { type: "application/octet-stream" }),
      `CallSummary_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  const handleSwitchView = () => {
    setViewMode((prev) => {
      const next = prev === "queue" ? "agent" : "queue";
      localStorage.setItem("viewMode", next);
      return next;
    });
  };

  /* ================= RENDER ================= */
  return (
  <div className="relative mb-6 rounded-2xl overflow-hidden">

    {/* 🔥 Top Glow */}
    <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-indigo-500 via-blue-500 to-purple-500 animate-pulse-glow" />

    {/* 🔥 Bottom Glow */}
    <div className="absolute bottom-0 left-0 w-full h-[2px] bg-gradient-to-r from-purple-500 via-blue-500 to-indigo-500 animate-pulse-glow" />

    <div className="bg-[#0f172a]/90 backdrop-blur-xl border border-white/10 rounded-2xl shadow-glow overflow-visible">

      {/* HEADER */}
      <div className="flex justify-between items-center px-4 py-3 border-b border-white/10">
        <h2 className="font-semibold text-lg glow-text">
          {viewMode === "queue"
            ? "Call Summary by MC"
            : "Call Summary by Agent"}
        </h2>

        <div className="flex gap-2">
          <button
            onClick={exportToExcel}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm bg-gradient-to-r from-indigo-600 to-blue-500 hover:scale-[1.05] transition shadow-glow"
          >
            <FileDown size={16} /> Export
          </button>

          <button
            onClick={handleSwitchView}
            className="px-4 py-2 rounded-xl text-sm bg-gradient-to-r from-purple-600 to-indigo-500 hover:scale-[1.05] transition shadow-glow"
          >
            Switch to {viewMode === "queue" ? "Agent" : "MC"}
          </button>
        </div>
      </div>

      {/* TABLE */}
      {isLoading ? (
        <div className="p-6 text-gray-400">Loading summary...</div>
      ) : summaryData.length === 0 ? (
        <div className="p-6 text-gray-500 text-center">
          No summary available.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-gray-300 border-collapse">
            <thead>
              <tr className="bg-[#111827]/70 text-xs uppercase tracking-wider text-gray-400">
                <th className="p-3 text-center">
                  {viewMode === "queue" ? "MC Name" : "Agent Name"}
                </th>
                <th className="p-3 text-center">Total</th>
                <th className="p-3 text-center">Accepted</th>
                <th className="p-3 text-center">Missed</th>
                <th className="p-3 text-center">Productive</th>
                <th className="p-3 text-center">Converted</th>
                <th className="p-3 text-center">MCO</th>
                <th className="p-3 text-center">Avg Length</th>
                <th className="p-3 text-center">Quality</th>
              </tr>
            </thead>

            <tbody>
              {summaryData.map((row, index) => {
                const isTotal =
                  row.queue_name === "Total" ||
                  row.agent_name === "Total";

                return (
                  <tr
                    key={index}
                    onClick={() => {
                      if (isTotal) return;

                      if (viewMode === "queue") {
                        onRowClick?.({
                          type: "queue",
                          value: row.queue_name,
                        });
                      } else {
                        onRowClick?.({
                          type: "agent",
                          value: row.agent_name,
                        });
                      }
                    }}
                    className={`transition-all duration-300 border-b border-white/5 ${
                      isTotal
                        ? "bg-gradient-to-r from-indigo-600/30 to-purple-600/30 text-white font-semibold"
                        : "cursor-pointer hover:bg-white/[0.04]"
                    }`}
                  >
                    <td className="p-3 text-center">
                      {row.queue_name || row.agent_name}
                    </td>
                    <td className="p-3 text-center">{row.total_calls}</td>
                    <td className="p-3 text-center">{row.accepted_calls}</td>
                    <td className="p-3 text-center">{row.missed_calls}</td>
                    <td className="p-3 text-center">{row.productive_calls}</td>
                    <td className="p-3 text-center">{row.converted_calls}</td>
                    <td className="p-3 text-center">
                      {row.MCO?.toFixed(2) || "0.00"}
                    </td>
                    <td className="p-3 text-center">
                      {Math.floor(row.avg_call_duration_seconds / 60)}m{" "}
                      {Math.floor(row.avg_call_duration_seconds % 60)}s
                    </td>
                    <td className="p-3 text-center">
                      {row.quality_calls}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  </div>
);

};

export default CallSummaryTable;
